import React, { useEffect, useState } from "react";
import { Event } from "@visitproof/types";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../../../design/dialog";
import { Button } from "../../../design/button";
import { Text } from "../../../design/text";
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../design/table";
import VisitEventCell from "../../VisitDetailPage/components/VisitEventCell";
import { Badge } from "../../../design/badge";
import {
  CalendarIcon,
  ClipboardDocumentListIcon,
} from "@heroicons/react/24/outline";
import { collection, query, where, orderBy, onSnapshot } from "firebase/firestore";
import { db } from "../../../firebase";

interface ViewEventsDialogProps {
  visitId: string;
  accountId: string;
  isOpen: boolean;
  onClose: () => void;
}

const ViewEventsDialog: React.FC<ViewEventsDialogProps> = ({
  visitId,
  accountId,
  isOpen,
  onClose,
}) => {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isOpen || !visitId || !accountId) {
      return;
    }

    setLoading(true);

    // Subscribe to events for this visit. accountId MUST be in the query — the
    // events read rule authorizes off accountId, so a query filtered only by
    // visitId is denied (surfacing as an empty "No events recorded" list even
    // when events exist).
    const eventsQuery = query(
      collection(db, "events"),
      where("accountId", "==", accountId),
      where("visitId", "==", visitId),
      orderBy("createdAt", "asc")
    );

    const unsubscribe = onSnapshot(
      eventsQuery,
      (snapshot) => {
        const eventData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as Event[];
        setEvents(eventData);
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching events:", error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [visitId, accountId, isOpen]);

  return (
    <Dialog open={isOpen} onClose={onClose} size="5xl">
      <DialogTitle>Visit Events</DialogTitle>
      <DialogBody>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Text className="text-zinc-500">Loading events...</Text>
          </div>
        ) : events.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-zinc-200 py-12 dark:border-zinc-700">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-zinc-100 dark:bg-zinc-800">
              <ClipboardDocumentListIcon className="h-6 w-6 text-zinc-400 dark:text-zinc-500" />
            </div>
            <Text className="mt-4 font-medium text-zinc-900 dark:text-zinc-100">
              No events recorded
            </Text>
            <Text className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
              No events were recorded during this visit.
            </Text>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Text className="font-medium">Events</Text>
              <Badge color="zinc">{events.length}</Badge>
            </div>
            <Table className="[--gutter:theme(spacing.6)] lg:[--gutter:theme(spacing.10)]">
              <TableHead>
                <TableRow>
                  <TableHeader>
                    <span className="flex items-center gap-1.5">
                      <CalendarIcon className="h-4 w-4" />
                      Date & Time
                    </span>
                  </TableHeader>
                  <TableHeader>Type</TableHeader>
                  <TableHeader>Details</TableHeader>
                  <TableHeader>Media</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {events.map((event) => (
                  <VisitEventCell key={event.id} event={event} />
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </DialogBody>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewEventsDialog;
